import LumaAccessiblePanel from '../admin/CoworkFormPanel';
import {Dialog as LumaAccessibleDialog, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
import React, { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { BannerSlide, deleteBannerSlide } from '../../services/landing/bannerSlideService';

interface DeleteBannerSlideModalProps {
    isOpen: boolean;
    slide: BannerSlide | null;
    onClose: () => void;
    onSuccess: () => void;
}

export const DeleteBannerSlideModal: React.FC<DeleteBannerSlideModalProps> = ({
    isOpen,
    slide,
    onClose,
    onSuccess,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    if (!isOpen || !slide) return null;

    const handleDelete = async () => {
        setLoading(true);
        setError('');
        try {
            await deleteBannerSlide(slide.id);
            onSuccess();
            onClose();
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            setError(err.response?.data?.detail || 'No se pudo eliminar la diapositiva.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <LumaAccessiblePanel data-luma-dialog="true" className="relative w-full max-w-md bg-white dark:bg-[#120a1f] rounded-2xl shadow-2xl border border-gray-200 dark:border-purple-900/40 p-6 overflow-hidden">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-purple-900/30 transition-colors"
                >
                    <X size={18} />
                </button>

                <div className="flex items-center gap-3.5 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-950/50 flex items-center justify-center text-red-600 dark:text-red-400 flex-shrink-0">
                        <AlertTriangle size={24} />
                    </div>
                    <div>
                        <LumaAccessibleTitle className="text-lg font-bold text-gray-900 dark:text-white">¿Eliminar Diapositiva?</LumaAccessibleTitle>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Esta acción no se puede deshacer</p>
                    </div>
                </div>

                <div className="p-3 bg-gray-50 dark:bg-purple-950/20 rounded-xl border border-gray-100 dark:border-purple-900/30 mb-5 text-sm text-gray-700 dark:text-gray-300">
                    <p className="font-semibold text-gray-900 dark:text-white mb-1">
                        Diapositiva #{slide.orden}: {slide.titulo} {slide.titulo_acento}
                    </p>
                    <p className="text-xs text-gray-500 line-clamp-2">
                        {slide.descripcion || 'Sin descripción'}
                    </p>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-xl text-red-600 text-xs">
                        {error}
                    </div>
                )}

                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        className="px-4 py-2 rounded-xl border border-gray-300 dark:border-purple-900/40 text-gray-700 dark:text-gray-300 font-semibold text-sm hover:bg-gray-50 dark:hover:bg-purple-900/20 transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={handleDelete}
                        disabled={loading}
                        className="px-5 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold text-sm shadow-lg shadow-red-900/25 transition-all disabled:opacity-50"
                    >
                        {loading ? 'Eliminando...' : 'Sí, eliminar'}
                    </button>
                </div>
            </LumaAccessiblePanel>
        </LumaAccessibleDialog>
    );
};

export default DeleteBannerSlideModal;

